import React, { lazy, Suspense, } from 'react';
import {
    Route,
    Navigate,
    BrowserRouter as Router,
    Routes,
} from 'react-router-dom';
import {
    Loader,
    SessionExpiredDialog,
    AccountLockedDialog,
} from '@components';
import ErrorBoundary from './ErrorBoundary';
import ScrollToTop from './scrollToTop';
import {
    PUBLIC_ROUTE,
    PRIVATE_ROUTE,
} from '@router/route.constants';
import { useSelector } from 'react-redux';

const publicRoutes = [
    {
        path: PUBLIC_ROUTE.PAGE_404,
        component: lazy(() => import('@pages/404/404')),
    },
    {
        path: PUBLIC_ROUTE.PAGE_500,
        component: lazy(() => import('@pages/500/500')),
    },
    {
        path: PUBLIC_ROUTE.LANDING,
        component: lazy(() => import('@pages/Splash/Splash')),
    },
    {
        path: PUBLIC_ROUTE.ONBOARDING,
        component: lazy(() => import('@pages/OnBoarding/OnBoarding')),
    },
    {
        path: PUBLIC_ROUTE.LOGIN,
        component: lazy(() => import('@pages/AuthenticationStack/Login/Login')),
    },
    {
        path: PUBLIC_ROUTE.REGISTER,
        component: lazy(() => import('@pages/AuthenticationStack/Register/Register')),
    },
    {
        path: PUBLIC_ROUTE.PASSWORD_LOGIN,
        component: lazy(() => import('@pages/AuthenticationStack/PasswordLogin/PasswordLogin')),
    },
    {
        path: PUBLIC_ROUTE.FORGOT_PASSWORD,
        component: lazy(() => import('@pages/AuthenticationStack/ForgotPassword/ForgotPassword')),
    },
    {
        path: `${PUBLIC_ROUTE.OTP_VERIFY}/:type`,
        component: lazy(() => import('@pages/AuthenticationStack/OtpVerify/OtpVerify')),
    },
    {
        path: PUBLIC_ROUTE.RESET_PASSWORD,
        component: lazy(() => import('@pages/AuthenticationStack/ResetPassword/ResetPassword')),
    },
    {
        path: PUBLIC_ROUTE.CREATE_PASSWORD,
        component: lazy(() => import('@pages/AuthenticationStack/CreatePassword/CreatePassword')),
    },
    {
        path: `${PUBLIC_ROUTE.HOME}/*`,
        component: lazy(() => import('@pages/Home/Home')),
    },
    {
        path: PUBLIC_ROUTE.MESSAGE,
        component: lazy(() => import('@pages/Message/Message')),
    },
    {
        path: PUBLIC_ROUTE.PROFILE,
        component: lazy(() => import('@pages/Profile/Profile'))
    },
    {
        path: PUBLIC_ROUTE.NOTIFICATION,
        component: lazy(() => import('@pages/Notification/Notification'))
    },
    {
        path: PUBLIC_ROUTE.NOTIFICATION_DETAILS,
        component: lazy(() => import('@pages/NotificationDetails/NotificationDetails')),
    },
    {
        path: PUBLIC_ROUTE.POCKET_MONEY,
        component: lazy(() => import('@pages/PocketMoney/PocketMoney')),
    },
    {
        path: PUBLIC_ROUTE.POCKET_MONEY_CONFIRM_PIN,
        component: lazy(() => import('@pages/Notification/PocketMoneyConfirmPin/PocketMoneyConfirmPin')),
    },
    {
        path: `${PUBLIC_ROUTE.TOP_WINNERS}/:child_game_id`,
        component: lazy(() => import('@pages/TopWinners/TopWinners')),
    },
    {
        path: PUBLIC_ROUTE.FRIEND_LIST,
        component: lazy(() => import('@pages/FriendList/FriendList')),
    },
    {
        path: PUBLIC_ROUTE.CHANGE_PIN,
        component: lazy(() => import('@pages/ChangePin/ChangePin')),
    },
    {
        path: PUBLIC_ROUTE.INVITE_LINK,
        component: lazy(() => import('@pages/InviteLink/InviteLink')),
    },
    {
        path: PUBLIC_ROUTE.EDIT_PROFILE,
        component: lazy(() => import('@pages/EditProfile/EditProfile')),
    },
    {
        path: PUBLIC_ROUTE.SPIN_HOME,
        component: lazy(() => import('@pages/SpinStack/SpinHome/SpinHome')),
    },
    {
        path: PUBLIC_ROUTE.SPIN_HISTORY,
        component: lazy(() => import('@pages/SpinStack/History/History')),
    },
    {
        path: PUBLIC_ROUTE.SPIN_RULE,
        component: lazy(() => import('@pages/SpinStack/Rules/Rules')),
    },
    {
        path: PUBLIC_ROUTE.SEARCH,
        component: lazy(() => import('@pages/Search/Search')),
    },
    {
        path: PUBLIC_ROUTE.APP_DOWNLOAD,
        component: lazy(() => import('@pages/AppDownload/AppDownload')),
    },
    {
        path: `${PUBLIC_ROUTE.CHILD_GAMES}/:main_game_id/:main_game_name`,
        component: lazy(() => import('@pages/ChildGames/ChildGames')),
    },
    {
        path: PUBLIC_ROUTE.EMAIL_REGISTER,
        component: lazy(() => import('@pages/AuthenticationStack/EmailRegister/EmailRegister')),
    },
    {
        path: PUBLIC_ROUTE.SHARE_REGISTER,
        component: lazy(() => import('@pages/ShareStack/ShareRegister/ShareRegister')),
    },
    {
        path: PUBLIC_ROUTE.PROMO_REGISTER,
        component: lazy(() => import('@pages/AuthenticationStack/PromoRegister/PromoRegister')),
    },
];

const privateRoutes = [
    {
        path: PRIVATE_ROUTE.IN_GAME,
        component: lazy(() => import('@pages/InGame/InGame')),
    },
    {
        path: PRIVATE_ROUTE.WALLET,
        component: lazy(() => import('@pages/WalletStack/Wallet/Wallet')),
    },
    {
        path: PRIVATE_ROUTE.TRANSACTIONS,
        component: lazy(() => import('@pages/WalletStack/TransactionHistory/TransactionHistory')),
    },
    {
        path: `${PRIVATE_ROUTE.ACCOUNT_TYPE}/:trx_type`,
        component: lazy(() => import('@pages/WalletStack/ChooseAccountType/ChooseAccountType')),
    },
    {
        path: `${PRIVATE_ROUTE.FILL_AMOUNT}/:trx_type`,
        component: lazy(() => import('@pages/WalletStack/FillAmount/FillAmount')),
    },
    {
        path: PRIVATE_ROUTE.PROCEED_TOPUP,
        component: lazy(() => import('@pages/WalletStack/ProceedTopUp/ProceedTopUp')),
    },
    {
        path: PRIVATE_ROUTE.WITHDRAW_ACCOUNTS,
        component: lazy(() => import('@pages/WalletStack/WithdrawAccounts/WithdrawAccounts')),
    },
    {
        path: `${PRIVATE_ROUTE.CREATE_UPDATE_ACCOUNT}/:mode`,
        component: lazy(() => import('@pages/WalletStack/WithdrawAccounts/CreateUpdateForm/CreateUpdateForm')),
    },
    {
        path: PRIVATE_ROUTE.CONFIRM_PIN,
        component: lazy(() => import('@pages/WalletStack/WithdrawAccounts/ConfirmPin/ConfirmPin')),
    },
    {
        path: PRIVATE_ROUTE.SHARE,
        component: lazy(() => import('@pages/ShareStack/Share/Share')),
    },
    {
        path: PRIVATE_ROUTE.SHARE_SUMMARY,
        component: lazy(() => import('@pages/ShareStack/ShareSummary/ShareSummary')),
    },
    {
        path: PRIVATE_ROUTE.SHARE_HISTORY,
        component: lazy(() => import('@pages/ShareStack/ShareHistory/ShareHistory')),
    },
    {
        path: PRIVATE_ROUTE.SHARE_TERMS,
        component: lazy(() => import('@pages/ShareStack/ShareTerms/ShareTerms')),
    },
    {
        path: PRIVATE_ROUTE.PROMO_CODE_HISTORY,
        component: lazy(() => import('@pages/PromoCodeHistory/PromoCodeHistory')),
    },
    {
        path: PRIVATE_ROUTE.PROMO_TERMS,
        component: lazy(() => import('@pages/PromoTerms/PromoTerms')),
    },
    {
        path: PRIVATE_ROUTE.GAME_LOST_REQUEST,
        component: lazy(() => import('@pages/GameLostRequest/GameLostRequest'))
    },
    {
        path: PRIVATE_ROUTE.GAME_LOST_REQUEST_HISTORY,
        component: lazy(() => import('@pages/GameLostRequestHistory/GameLostRequestHistory'))
    },
    {
        path: PRIVATE_ROUTE.FREEZE_BALANCES,
        component: lazy(() => import('@pages/FreezeBalances/FreezeBalances'))
    },
    {
        path: PRIVATE_ROUTE.CHECK_PAYMENT_DATA,
        component: lazy(() => import('@pages/WalletStack/CheckPaymentData/CheckPaymentData')),
    },
];

const PrivateRoute = ({ children, }) => {
    const isLoggedIn = useSelector((state) => state.auth.token);

    if (!isLoggedIn) {
        return (
            <Navigate
                to={`/${PUBLIC_ROUTE.LOGIN}`}
                replace={true}
            />
        );
    }

    return children;
}

const AppRoutes = () => {
    return (
        <ErrorBoundary>
            <Suspense fallback={<Loader />}>
                <Router>
                    <ScrollToTop />
                    <Routes>
                        {publicRoutes.map((route, index) => (
                            <Route
                                key={index}
                                exact={route.exact}
                                path={`/${route.path}`}
                                element={<route.component />}
                            />
                        ))}
                        {privateRoutes.map((route, index) => (
                            <Route
                                key={index}
                                exact={route.exact}
                                path={`/${route.path}`}
                                element={
                                    <PrivateRoute>
                                        <route.component />
                                    </PrivateRoute>
                                }
                            />
                        ))}
                        <Route path='*' element={<Navigate to={PUBLIC_ROUTE.PAGE_404} />} />
                    </Routes>
                    <SessionExpiredDialog />
                    <AccountLockedDialog />
                </Router>
            </Suspense>
        </ErrorBoundary>
    );
}

export default AppRoutes;